import {
  BankOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  FileSearchOutlined,
  IdcardOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  ConfigProvider,
  Divider,
  Input,
  Layout,
  List,
  Menu,
  Modal,
  Row,
  Space,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import React, { useState } from 'react';
import './App.css';

const { Header, Sider, Content, Footer } = Layout;
const { Title, Paragraph, Text } = Typography;

const evcHighlights = [
  {
    label: 'Function',
    value:
      'Automates and manages name-based background checks for background investigations, national security, and personnel security functions.',
  },
  {
    label: 'Formal name',
    value:
      'Often associated with the National Name Check Program within the FBI Information Management Division.',
  },
  {
    label: 'Fee structure',
    value:
      'The July 2023 user fee schedule includes cost reimbursement for services and automation funding for technology modernization.',
  },
  {
    label: 'Integration',
    value:
      'Supports federal agency vetting needs and works with systems such as the Law Enforcement Enterprise Portal.',
  },
];

const unofficialSummaryCards = [
  {
    label: 'Purpose',
    title: 'Plain-language program brief',
    text:
      'This page summarizes public EVC, NNCP, CJIS, and Program Manager role context in one readable dashboard.',
  },
  {
    label: 'Status',
    title: 'Unofficial reference',
    text:
      'This is not an FBI, ANASEC, DHS, or government publication and should be treated as a personal research and application-preparation aid.',
  },
  {
    label: 'Use case',
    title: 'Role and resume alignment',
    text:
      'The content connects mission context, role requirements, clearance language, and resume evidence for interview and application preparation.',
  },
];

const unofficialSummaryPoints = [
  'EVC is presented as a vetting-oriented mission context tied to FBI name-based background checks and federal agency security functions.',
  'NNCP content explains how FBI name checks differ from CJIS fingerprint-based criminal-history checks and NCIC law-enforcement records.',
  'The Program Manager section summarizes the ANASEC role scope, including stakeholder coordination, risk management, quality control, staffing, and metrics.',
  'The Resume Match section maps Russell Riehl’s federal program management, DHS/USCIS, DevSecOps, data strategy, and active TS/SCI experience to the role.',
  'All content should be verified against official agency sources, current job postings, and clearance/investigation records before use in an application.',
];

const evcSummaryDetails = [
  'The Enterprise Vetting Center is an FBI Information Management Division program that centralizes certain name-based, non-criminal-justice background checks for federal agencies.',
  'It provides a fee-for-service vetting capability that uses FBI holdings and tools to support suitability, credentialing, facility access, contracts, and similar administrative screening needs.',
  'The EVC context is separate from traditional criminal investigations and routine law-enforcement queries.',
];

const evcFederalRegisterPoints = [
  'A 2023 Federal Register notice explains that EVC charges user fees to federal agencies requesting name-based vetting services.',
  'The fee schedule is designed to recover FBI costs for running and maintaining the vetting capability.',
  'The notice describes the service as name-based, non-criminal-justice background checks for other agencies.',
  'Public notice content emphasizes scope and fees rather than detailed internal technical workflow or algorithms.',
];

const evcVettingRelationship = [
  'EVC sits in the same broad vetting ecosystem as NNCP because both leverage FBI records and name-based searches to support decisions by other agencies.',
  'NNCP is the long-standing FBI name-check program associated with the Records Management Division.',
  'EVC is framed as an enterprise-facing background-check service with explicit cost-recovery user fees for client agencies.',
  'Both are primarily non-criminal-justice services used to support eligibility, suitability, and risk decisions rather than to open or manage criminal cases.',
];

const evcPublicInfoLimits = [
  'Public sources provide useful scope, fee, and mission context but do not provide a complete technical architecture.',
  'It is reasonable to infer overlap with FBI records indices and name-based search processes, but internal workflow details should not be assumed without official confirmation.',
  'For application or interview use, describe EVC at the mission and public-notice level unless an official source provides deeper process detail.',
];

const evcDistinctionCards = [
  {
    label: 'Mission type',
    title: 'Non-criminal-justice vetting',
    text:
      'EVC supports suitability, credentialing, and administrative screening rather than criminal investigations or law-enforcement case management.',
  },
  {
    label: 'Funding model',
    title: 'Fee-for-service',
    text:
      'EVC is funded by the agencies that use it. Program performance, timeliness, and quality directly affect client trust, renewal, and funding.',
  },
  {
    label: 'Client scope',
    title: 'Enterprise-facing',
    text:
      'EVC is designed to serve multiple federal agencies at scale through a centralized service, not just law-enforcement or intelligence consumers.',
  },
];

const evcSummarySources = [
  {
    label: 'EVC Federal Register fee schedule',
    href: 'https://www.federalregister.gov/documents/2023/05/23/2023-10902/fbi-information-management-division-enterprise-vetting-center-user-fee-schedule',
  },
  {
    label: 'NNCP Federal Register notice',
    href: 'https://www.federalregister.gov/documents/2019/12/30/2019-28165/federal-bureau-of-investigation-records-management-division-national-name-check-program-section',
  },
  {
    label: 'FBI Name Check overview',
    href: 'https://en.wikipedia.org/wiki/FBI_Name_Check',
  },
];

const backgroundSystemCards = [
  {
    label: 'Primary FBI database',
    title: 'Next Generation Identification',
    text:
      'NGI is the FBI’s major biometric and identity platform, successor to IAFIS, and links fingerprints and other biometrics to identity and criminal-history records.',
  },
  {
    label: 'Law-enforcement index',
    title: 'National Crime Information Center',
    text:
      'NCIC is a national index for wanted persons, protection orders, missing persons, stolen property, warrants, and other real-time law-enforcement flags.',
  },
  {
    label: 'Personnel security services',
    title: 'e-QIP, DCSA, and Rap Back',
    text:
      'Personnel-security workflows combine investigative data collection, DCSA case processes, adjudication, and Rap Back continuous notification rather than relying on a single database.',
  },
];

const ngiCoreSystems = [
  {
    name: 'NGI / Next Generation Identification',
    description:
      'Primary biometric and identity system containing fingerprints and other biometrics linked to identity and criminal-history information for authorized background-check and law-enforcement uses.',
  },
  {
    name: 'NCIC / National Crime Information Center',
    description:
      'National index queried for active warrants, wanted persons, protection orders, missing persons, stolen property, and similar law-enforcement flags that may affect vetting decisions.',
  },
  {
    name: 'FBI Identification Record / III',
    description:
      'Often understood as the FBI rap sheet, compiled from fingerprint-linked arrests, dispositions, federal employment, naturalization, military, and related identity-history records.',
  },
  {
    name: 'DCSA / e-QIP workflows',
    description:
      'Web-based investigative data collection and transmission workflows that feed personnel-security, suitability, and clearance investigations.',
  },
  {
    name: 'Rap Back',
    description:
      'Search, retain, and notify service that can retain fingerprints after an initial check and notify authorized agencies of later law-enforcement contact.',
  },
];

const ngiRepositoryFiles = [
  {
    name: 'Criminal and Civil Files',
    description:
      'Logical repositories for biometric and associated biographic data tied to criminal records and authorized civil submissions such as employment, licensing, and background checks.',
  },
  {
    name: 'Special Population Cognizant Files',
    description:
      'SPC files hold records for defined special populations that require specific handling, monitoring, or notification rules, including security-sensitive populations.',
  },
  {
    name: 'Unsolved Latent File',
    description:
      'ULF stores latent fingerprint or palmprint images and extracted features from crime scenes that have not yet been associated with a known identity.',
  },
  {
    name: 'Unsolved Iris File',
    description:
      'UIF stores iris images and related data when the subject is unknown or the iris record has not yet been linked to an existing identity.',
  },
  {
    name: 'Unsolved Photo File',
    description:
      'UPF maintains facial images suitable for face-recognition searches when the subject has not yet been identified.',
  },
  {
    name: 'Immigration Violator File',
    description:
      'IVF supports immigration-related checks and notification workflows, including routing certain NGI event notifications to DHS support channels.',
  },
];

const ngiRecordStructure = [
  {
    category: 'Unique identifiers',
    details:
      'FBI Universal Control Number as the primary identity key, plus state identification numbers, agency identifiers, transaction IDs, and other external references.',
  },
  {
    category: 'Biometric data',
    details:
      'Ten-print fingerprint images and minutiae, latent and palm prints, facial images, iris images, and other modality references when authorized and available.',
  },
  {
    category: 'Biographic attributes',
    details:
      'Name, aliases, date of birth, sex, race, descriptive traits, citizenship, and administrative attributes required for specific criminal-justice or civil programs.',
  },
  {
    category: 'Criminal history and events',
    details:
      'Arrest events, charges, dispositions, Criminal History Record Information, submission source metadata, and timestamps tied back to the identity record.',
  },
  {
    category: 'Notification metadata',
    details:
      'Rap Back and special-file event metadata used to generate notifications when new qualifying activity occurs on a retained identity.',
  },
];

const ngiFileDefinitionPoints = [
  'Within NGI, “file” means a logical repository for a record type or use case, not a single flat database table.',
  'A system inventory entry can define NGI as an FBI-managed biometric and identity repository containing fingerprints, palm prints, facial images, iris images, biographic data, and criminal-history information.',
  'The repository supports criminal justice, national security, personnel security, and authorized civil background-check purposes.',
  'NGI is keyed conceptually by FBI identity references such as the Universal Control Number, with related state, agency, biometric, and transaction identifiers.',
  'At service level, NGI supports one-to-many identification searches, one-to-one verification checks, and notification events such as Rap Back or special-file alerts.',
  'Because the FBI does not publish a full relational schema, detailed catalog fields should be marked as conceptual and public-source-derived unless validated by an official interface specification.',
];

const ngiCapabilityPoints = [
  'NGI is a modular, extensible biometric platform that expanded IAFIS from a fingerprint system into a multimodal identification and background-check backbone.',
  'Supported modalities include ten-print and latent fingerprints, palm prints, face-recognition-ready photos, iris scans, and additional biometric modalities as the platform evolves.',
  'The architecture is person-centric: multiple biometric records, photos, and biographic attributes can be linked to a single subject identity and associated history.',
  'Public descriptions note high-throughput ten-print processing and improved match accuracy as NGI modernized IAFIS-era processing.',
  'The service portfolio includes rapid field identification, national palm print services, latent fingerprint tools, facial investigative searches, photo searches, and external federal biometric linkages.',
];

const ncicArchitecturePoints = [
  'NCIC operates as a transactional, near-real-time index for law-enforcement information such as wanted persons, missing persons, protection orders, sex offenders, violent persons, and stolen property.',
  'It is designed for continuous 24/7 availability so authorized officers and agencies can query records during field encounters and investigations.',
  'Public references describe long-standing interoperability requirements, including support for TCP/IP, ASCII bisynchronous, and IBM SNA-style connectivity patterns.',
  'The security model is governed by CJIS Security Policy requirements, including encryption, access control, auditing, and minimum controls for systems handling Criminal Justice Information.',
  'NCIC hits are operational leads and must be verified with the originating agency before enforcement action is taken.',
];

const rapBackWorkflowPoints = [
  'Rap Back implements a search, retain, and notify model for authorized non-criminal-justice fingerprint submissions.',
  'Search means fingerprints are submitted for applicants, employees, licensees, volunteers, or other authorized populations through a state SIB or directly to NGI.',
  'Retain means NGI keeps the fingerprints as new identities or matches them to existing identities for future monitoring.',
  'Notify means NGI sends authorized agencies notifications of future triggering events such as arrests or other law-enforcement contact.',
  'A consuming system should store subscription IDs, triggering event types, enrollment dates, expiration dates, and correlation fields such as FBI ID or NGI identity references.',
  'Rap Back notifications should enter through an event-driven receipt path that correlates events to subjects and creates follow-up investigation or adjudication tasks.',
];

const ngiScalePoints = [
  'NGI is publicly described as one of the largest biometric repositories, with public sources citing more than 141 million individual records.',
  'The repository includes criminal submissions and civilian submissions such as employment, licensing, DMV, and background-check fingerprints.',
  'Personnel-security and suitability checks operate at high annual volume, with public analysis estimating around 1.8 million workers undergoing FBI background checks annually.',
  'NCIC is important for nationwide checks but is not a complete national criminal-history database, so results often need to be supplemented by other sources.',
  'The practical scale is hundreds of millions of identity-related records across multi-decade federal, state, local, criminal, and civil-submission feeds.',
];

const ngiIdentifierKeys = [
  'Primary biometric keys include fingerprint templates and associated biometric identifiers that link to NGI identity and criminal-history records.',
  'Name-based searches use full legal name, aliases, date of birth, Social Security Number, place of birth, and related demographic identifiers.',
  'A typical workflow starts with name, date of birth, SSN, and alias matching to identify candidate records across repositories.',
  'Where fingerprints are available or required, biometrics confirm identity and retrieve the associated NGI record and FBI number.',
  'Rap Back subscriptions add subscription keys, technical fingerprint classification metadata, and event-trigger configuration that bind future notifications to retained prints.',
];

const ngiMissionUses = [
  'Identity verification: confirming the subject is who they claim to be and connecting known aliases to the same identity.',
  'Criminal history retrieval: pulling arrests, charges, dispositions, and rap-sheet information from FBI and state-fed repositories.',
  'Watchlist and warrant checks: querying NCIC and related sources for active warrants, protection orders, and security-relevant flags.',
  'Continuous evaluation: using Rap Back-style services to notify authorized agencies after the initial check when new law-enforcement contact occurs.',
  'Adjudication support: returning information to personnel-security, suitability, and clearance workflows for human review and decision-making.',
];

const ngiArchitecturePoints = [
  'The exact internal FBI technical stack is not public in commercial-stack terms, but the systems imply high-availability, high-throughput, transactional platforms.',
  'Large-scale identity repositories require strict auditability, chain of custody, role-based access, encryption, and controlled data-sharing agreements.',
  'Integrations are likely message-oriented and API-driven, with secure feeds from federal, state, local, and partner systems.',
  'e-QIP is publicly described as a secure web-based automated system for entering, updating, and transmitting investigative data.',
  'A consuming automation platform would typically include a secure web/API tier, request and subject database, audit-log/event store, integration layer, and workflow engine.',
  'The system should treat NGI, NCIC, III, Rap Back, DCSA, and state SIBs as external authoritative sources while retaining only authorized subject, request, transaction, audit, and adjudication records locally.',
];

const ngiSchemaDesignPoints = [
  'Subject identity: internal immutable subject ID with one-to-many names, aliases, SSNs, DoD IDs, and other identifiers.',
  'Biometric mapping: one-to-one or one-to-many links to FBI numbers, fingerprint identifiers, or other external biometric references.',
  'Investigation lifecycle: each request keyed to subject, investigation type, sponsoring organization, status, dates, and external transaction IDs.',
  'Query transactions: separate records for NGI, NCIC, Rap Back, or other system calls, including timestamps, response status, and audit metadata.',
  'Adjudication artifacts: normalized derogatory information, dispositions, analyst decisions, risk notes, and full source-system traceability.',
  'Derogatory items should separate source truth from policy interpretation by tracking item type, source system, jurisdiction, offense/date fields, severity, and disposition.',
  'Adjudication decisions should store outcome, applied guidelines, adjudicator, timestamps, reasoning metadata, and evidence references with full auditability.',
];

const ngiWorkflowFlows = [
  {
    name: 'Onboarding / initial check',
    description:
      'Collect subject PII, ingest HR or clearance-system data, collect fingerprints through an authorized channel, submit non-criminal-justice checks, and store external transaction IDs.',
  },
  {
    name: 'Response ingestion and correlation',
    description:
      'Receive NGI, NCIC, state SIB, or related responses; correlate by FBI ID, subscription ID, external transaction ID, or person identifiers; normalize results into case records.',
  },
  {
    name: 'Adjudication workflow',
    description:
      'Route derogatory items to analysts, apply agency-specific guidelines, enforce separation of duties, record decisions, and retain a complete audit trail.',
  },
  {
    name: 'Rap Back / continuous evaluation',
    description:
      'Enroll eligible subjects after favorable adjudication, store subscription metadata, receive future event notifications, and create follow-up investigations.',
  },
  {
    name: 'Deprovisioning and retention',
    description:
      'Cancel Rap Back subscriptions and archive, retain, or destroy records based on CJIS, privacy, and agency retention requirements when access or trust ends.',
  },
];

const techStackLayers = [
  {
    layer: 'UI / API',
    choice: 'React or Angular front end with Java/.NET REST services',
    rationale:
      'Fits complex federal workflows, structured adjudication, and secure browser-based case management.',
  },
  {
    layer: 'Core database',
    choice: 'PostgreSQL, SQL Server, or Oracle',
    rationale:
      'Supports identity, case, transaction, adjudication, and audit schemas with strong transactional guarantees.',
  },
  {
    layer: 'Message / event bus',
    choice: 'Kafka, RabbitMQ, Azure Service Bus, or equivalent',
    rationale:
      'Decouples Rap Back notifications, NCIC/NGI integrations, retries, ordering, workflow events, and audit pipelines.',
  },
  {
    layer: 'Integration adapters',
    choice: 'CJIS-compliant web service clients and secure gateways',
    rationale:
      'Normalizes calls to NGI, NCIC, NICS, Rap Back, DCSA, state SIBs, or authorized partner services.',
  },
  {
    layer: 'Security and compliance',
    choice: 'PKI, MFA, RBAC/ABAC, HSM-backed keys, encryption, SIEM',
    rationale:
      'Required for CJIS-style access control, nonrepudiation, encryption, monitoring, and investigation auditability.',
  },
];

const ngiSources = [
  {
    label: 'DOJ NGI privacy impact assessment',
    href: 'https://www.justice.gov/sites/default/files/jmd/legacy/2014/07/16/fbi-2011-ngi.pdf',
  },
  {
    label: 'NIST NGI glossary',
    href: 'https://www.nist.gov/glossary-term/28031',
  },
  {
    label: 'FBI NGI update',
    href: 'https://info.publicintelligence.net/FBI-NGI-Update.pdf',
  },
  {
    label: 'PrivacySOS NGI fact sheet',
    href: 'https://privacysos.org/sites/all/files/Scomm_NGI_factsheet.pdf',
  },
  {
    label: 'EPIC NGI overview',
    href: 'https://archive.epic.org/privacy/surveillance/spotlight/ngi.html',
  },
  {
    label: 'HigherGov NGI program',
    href: 'https://www.highergov.com/it-program/fbi-next-generation-identification-ngi-11946/',
  },
  {
    label: 'EFF NGI database',
    href: 'https://www.eff.org/cases/fbis-next-generation-identification-biometrics-database',
  },
  {
    label: 'NGI overview',
    href: 'https://en.wikipedia.org/wiki/Next_Generation_Identification',
  },
  {
    label: 'BiometricUpdate NGI overview',
    href: 'https://www.biometricupdate.com/201311/ngi-a-closer-look-at-the-fbis-billion-dollar-biometric-program',
  },
  {
    label: 'PBS NGI / Rap Back background',
    href: 'https://www.pbs.org/newshour/show/employers-can-use-fbi-database-real-time-background-checks',
  },
  {
    label: 'Biometrics4ALL FBI background checks',
    href: 'https://www.biometrics4all.com/articles-insights/essentials-of-fbi-background-checks',
  },
  {
    label: 'USDA APHIS personnel security / e-QIP',
    href: 'https://www.aphis.usda.gov/aphis/ourfocus/business-services/new-employee-onboarding/personnel-security',
  },
  {
    label: 'Peopletrail name vs fingerprint checks',
    href: 'https://peopletrail.com/difference-background-check-fingerprint/',
  },
  {
    label: 'BINC FBI record overview',
    href: 'https://mybinc.com/blog/what-databases-are-used-for-different-types-of-background-checks/',
  },
  {
    label: 'DCSA Rap Back FIN',
    href: 'https://www.dcsa.mil/Portals/91/Documents/pv/GovHRSec/FINs/FY19/fin-19-02.pdf',
  },
  {
    label: 'NCSBN Rap Back overview',
    href: 'https://www.ncsbn.org/public-files/NGI_Rap_Back_Overview_Presentation_for_National_Council_of_State_Boards_of_Nursing_2014_05_21.pdf',
  },
  {
    label: 'DOI Rap Back Q&A',
    href: 'https://ibc.doi.gov/sites/default/files/HRD/files/Federal-Bureau-of-Investigations-Rap-Back-QAs.pdf',
  },
  {
    label: 'CJIS / NCIC compliance',
    href: 'https://www.psportals.com/blog/ncic-compliance-and-security/',
  },
  {
    label: 'NCIC event-driven modernization',
    href: 'https://orangeslices.ai/8a-small-business-adds-fbi-cjis-ncic-event-driven-microservices-architecture-support-win/',
  },
  {
    label: 'Azure NICS integration example',
    href: 'https://www.microsoft.com/en/customers/story/23003-vermont-judiciary-azure-sql',
  },
];

const roleStats = [
  { label: 'Employer', value: 'ANASEC, Inc.' },
  { label: 'Location', value: 'Winchester, VA' },
  { label: 'Salary', value: '$140,000 / year' },
  { label: 'Schedule', value: 'Full-time, in office' },
];

const responsibilities = [
  'Manage research analysts, a deputy program manager, and a quality assurance manager.',
  'Serve as the primary point of contact for FBI contracting stakeholders.',
  'Assess risk related to schedule, performance, quality, change, and resources.',
  'Provide metrics and analysis that support leadership decision-making.',
  'Coordinate planning, briefings, staffing priorities, and operational coverage.',
  'Exercise quality control over contract deliverables, budget, and PWS requirements.',
];

const qualifications = [
  'Bachelor’s degree required.',
  '5+ years across budget, schedule, quality, and risk management.',
  'Recent experience with vetting and analysis of U.S. Government records and databases.',
  'Personnel security experience that includes scheduling and supervising project personnel.',
  'Active U.S. Government security clearance with T5, T5R, and CE/CV entry.',
];

const cjisOverview = [
  'CJIS stands for Criminal Justice Information Services and is headquartered in Clarksburg, West Virginia.',
  'It functions as a high-tech data and services hub for federal, state, local, tribal, and international law enforcement agencies.',
  'Established in 1992 as an evolution of the FBI’s earlier Identification Division, it is now the FBI’s largest division by size and scope.',
];

const cjisSystems = [
  {
    name: 'National Crime Information Center (NCIC)',
    description:
      'Nationwide database of wanted persons, missing persons, stolen property, and protection orders.',
  },
  {
    name: 'Next Generation Identification (NGI) / IAFIS',
    description:
      'Large-scale biometric systems for fingerprints, palm prints, facial recognition, iris, and other identifiers.',
  },
  {
    name: 'National Instant Criminal Background Check System (NICS)',
    description:
      'Used to run instant firearm purchase eligibility checks.',
  },
  {
    name: 'Uniform Crime Reporting (UCR)',
    description:
      'Collects and publishes national crime statistics, including general crime, hate crime, use-of-force, and incident data.',
  },
  {
    name: 'LEEP and N-DEx',
    description:
      'Portals and data-exchange platforms for sharing investigative and case data among agencies.',
  },
];

const cjisImportance = [
  'Underpins day-to-day policing, including dispatch checks, warrant hits, vehicle stops, and missing-persons work.',
  'Supports firearm purchase background checks and sensitive employment screenings requiring fingerprint-based checks.',
  'Defines security controls commonly referenced as CJIS compliance for protecting criminal justice information.',
  'Provides near-real-time access to criminal history, identity history, stolen property, and other investigative data.',
];

const cjisSources = [
  {
    label: 'Wikipedia',
    href: 'https://en.wikipedia.org/wiki/FBI_Criminal_Justice_Information_Services_Division',
  },
  {
    label: 'CJIS',
    href: 'https://www.cjis.gov',
  },
  {
    label: 'EasyITGuys',
    href: 'https://easyitguys.com/compliance/criminal-justice-information-services-division-cjis/',
  },
];

const nncpSummaryCards = [
  {
    label: 'Program owner',
    title: 'FBI Records Management Division',
    text:
      'NNCP responds to name-check requests from federal agencies, Congress, federal courts, friendly foreign police and intelligence services, and criminal justice partners.',
  },
  {
    label: 'Core function',
    title: 'Name-based record search',
    text:
      'The program searches FBI records for references to a person’s name to support security vetting, background checks, and investigative needs.',
  },
  {
    label: 'Operational distinction',
    title: 'Different from CJIS biometrics',
    text:
      'NNCP is name-based and searches broader FBI references, while CJIS fingerprint checks focus on biometric criminal-history records.',
  },
];

const nncpWhat = [
  'NNCP is run by the FBI’s Records Management Division and supports federal agencies, Congress, federal courts, friendly foreign police and intelligence services, and state/local criminal justice agencies.',
  'The program traces to authorities created under Executive Order 10450 and evolved into a formal program for large-scale name-based background screening.',
  'It searches for references to a person’s name and provides responsive results to requesting agencies for security vetting, background checks, and investigative support.',
];

const nncpProcess = [
  'NNCP searches the FBI Central Records System using the Universal Index, which indexes names across investigative, administrative, applicant, and personnel files.',
  'Searches include close phonetic variants, permutations, alternate spellings, and aliases rather than only exact-name matches.',
  'If the Universal Index returns no records, the check is typically auto-cleared and many “no record” responses are returned electronically within roughly 48–72 hours.',
  'If the initial search returns hits, NNCP staff retrieve related electronic and physical files from relevant field offices or storage locations.',
  'An analyst manually reviews returned files to confirm whether they pertain to the subject and whether they contain derogatory or otherwise relevant information.',
  'Additional files may be requested and reviewed iteratively until the analyst can make a determination and respond to the requesting agency.',
];

const nncpUses = [
  'Security vetting for federal employment, appointments, and clearances.',
  'Screening for visitors to sensitive facilities or events, including White House-related events.',
  'Federal and state criminal investigations, counterintelligence, and counterterrorism work.',
  'Immigration benefits processing for visas, permanent residence, and naturalization cases handled by USCIS.',
  'Since late 2002, USCIS became one of NNCP’s largest customers by requiring comprehensive FBI name checks on permanent residence and naturalization applicants.',
  'USCIS and the FBI later implemented backlog-reduction goals, including processing 98% of name checks within 30 days and the remainder within 90 days by mid-2009.',
];

const nncpDistinctions = [
  'NNCP is name-based and searches FBI records for any reference to a person, including as a subject, associate, witness, or other mention.',
  'Fingerprint-based criminal history checks through CJIS NGI/IAFIS focus on arrest and disposition records tied to biometric identifiers.',
  'NCIC is a real-time law-enforcement database of wanted persons, missing persons, and stolen property, while NNCP mines broader FBI records for references to an individual.',
];

const nncpPolicyIssues = [
  'Heavy reliance on full NNCP vetting, especially in immigration cases, has been criticized for creating substantial processing delays.',
  'Lawsuits and congressional hearings highlighted cases where applicants waited years because of unresolved name checks.',
  'Agencies continue to argue that full NNCP checks provide national security value because they can surface information standard criminal-history checks and automated watchlist hits may miss.',
];

const nncpSources = [
  {
    label: 'FBI Name Check',
    href: 'https://en.wikipedia.org/wiki/FBI_Name_Check',
  },
  {
    label: 'Federal Register',
    href: 'https://www.federalregister.gov/documents/2019/12/30/2019-28165/federal-bureau-of-investigation-records-management-division-national-name-check-program-section',
  },
  {
    label: 'IT Law Wiki',
    href: 'https://itlaw.fandom.com/wiki/National_Name_Check_Program',
  },
  {
    label: 'Duke Law Review',
    href: 'https://scholarship.law.duke.edu/cgi/viewcontent.cgi?article=1202&context=dltr',
  },
  {
    label: 'NAFSA',
    href: 'https://www.nafsa.org/professional-resources/browse-by-interest/uscis-fbi-eliminate-national-name-check-backlog',
  },
];

const resumeMatchStats = [
  { label: 'Federal IT experience', value: '18 years' },
  { label: 'Largest team led', value: '150+' },
  { label: 'USCIS program', value: '$68M' },
  { label: 'Clearance', value: 'DHS / DoD TS/SCI' },
];

const leadershipMatches = [
  {
    requirement:
      'Manage research analysts, a deputy program manager, and a quality assurance manager.',
    evidence:
      'Led 70+ staff on the USCIS Data Strategy program and 150+ team members across DevSecOps, development, and operations for DHS USCIS Verification.',
    strength: 'Strong',
  },
  {
    requirement:
      'Serve as the primary point of contact for FBI contracting stakeholders.',
    evidence:
      'Served as primary point of contact for USCIS leadership with weekly reviews, ad hoc meetings, executive briefings, and contractor-team coordination.',
    strength: 'Strong',
  },
  {
    requirement:
      'Assess risk related to schedule, performance, quality, change, and resources.',
    evidence:
      'Managed schedule, risk, quality standards, SAFe roadmaps, delivery metrics, DevSecOps gates, and 100% on-time deliverables.',
    strength: 'Strong',
  },
  {
    requirement:
      'Provide metrics and analysis that support leadership decision-making.',
    evidence:
      'Delivered DHS data strategy, Collibra metadata traceability, BI/DW reporting, dashboards, compliance metrics, and executive decision support.',
    strength: 'Strong',
  },
  {
    requirement:
      'Coordinate planning, briefings, staffing priorities, and operational coverage.',
    evidence:
      'Coordinated executive briefings, PI planning, stakeholder reviews, cross-team dependencies, staffing priorities, and workload execution.',
    strength: 'Strong',
  },
  {
    requirement:
      'Exercise quality control over contractor staff to deliver services and products on budget and schedule.',
    evidence:
      'Organized, directed, and coordinated contract activities; supervised staff; oversaw deliverables; and ensured USCIS/DHS procedural compliance.',
    strength: 'Strong',
  },
];

const qualificationMatches = [
  {
    requirement: 'Bachelor’s degree required.',
    evidence: 'Bachelor of Computer Science, University of New Brunswick.',
    strength: 'Strong',
  },
  {
    requirement:
      '5+ years in program management across budget, schedule, quality, and risk.',
    evidence:
      'PMP with multiple federal Program Manager roles, $68M USCIS program leadership, Agile/SAFe delivery, risk management, and quality oversight.',
    strength: 'Strong',
  },
  {
    requirement:
      'Recent experience related to vetting and analysis of U.S. Government records and databases.',
    evidence:
      'DHS USCIS Verification, E-Verify SSA TNC Automated Response, USCIS Data Strategy, DHS ICE Collibra DGC, and federal data governance experience.',
    strength: 'Strong',
  },
  {
    requirement:
      'Personnel security-related specialty including scheduling and supervising project personnel.',
    evidence:
      'Strong scheduling and supervisory background across DHS verification and federal records/data programs; personnel-security language should be made explicit where accurate.',
    strength: 'Partial',
  },
  {
    requirement: 'Active U.S. Government security clearance.',
    evidence:
      'Active DHS / DoD Top Secret / SCI. Confirm T5/T5R and CE/CV mapping or reciprocity during application processing.',
    strength: 'Strong',
  },
];

const decisionMakerStats = [
  {
    label: 'Federal mission delivery',
    value: '18 years',
    detail:
      'Experience leading federal IT, data, verification, DevSecOps, and mission-support programs for DHS and other government stakeholders.',
  },
  {
    label: 'Largest team led',
    value: '150+',
    detail:
      'Scale of contractor and delivery leadership across development, operations, DevSecOps, data, and cross-functional federal program teams.',
  },
  {
    label: 'Program scale',
    value: '$68M',
    detail:
      'USCIS Data Strategy program leadership with schedule, risk, quality, staffing, stakeholder, and executive reporting accountability.',
  },
  {
    label: 'Clearance posture',
    value: 'TS/SCI',
    detail:
      'Active DHS / DoD Top Secret / SCI clearance supporting trusted access, mission discretion, and readiness for sensitive environments.',
  },
];

const executiveValueCards = [
  {
    label: 'Mission understanding',
    title: 'Disciplined execution for sensitive vetting work',
    text:
      'EVC support requires accuracy, discretion, responsiveness, auditability, and trust. The value proposition is disciplined program control around people, process, quality, metrics, and stakeholder confidence.',
  },
  {
    label: 'Day-one leadership',
    title: 'Low-risk program transition',
    text:
      'Russell brings cleared federal delivery experience, large-team leadership, DHS verification context, executive communications, and contractor-performance discipline that can stabilize execution quickly.',
  },
  {
    label: 'Program improvement',
    title: 'Measurable operational value',
    text:
      'The focus is stronger reporting, clearer risks, better QA feedback loops, predictable deliverables, transparent staffing coverage, and data-driven decisions for ANASEC and FBI stakeholders.',
  },
];

const first90DayPlan = [
  {
    phase: 'Days 1-30',
    title: 'Learn, align, and stabilize',
    actions: [
      'Meet FBI COR/CO, ANASEC leadership, deputy PM, QA manager, analyst leads, and operational stakeholders.',
      'Review PWS scope, deliverables, SLAs, staffing model, risk register, reporting cadence, quality standards, and escalation paths.',
      'Understand case flow, bottlenecks, workload drivers, quality trends, stakeholder pain points, and current performance baseline.',
    ],
  },
  {
    phase: 'Days 31-60',
    title: 'Improve execution rhythm',
    actions: [
      'Tighten dashboards, status reports, action tracking, risk/issue/change logs, and leadership decision-support metrics.',
      'Standardize analyst performance expectations, QA feedback loops, staffing coverage, cross-training needs, and recurring briefings.',
      'Identify process friction, training gaps, dependency risks, and opportunities to improve timeliness without compromising quality.',
    ],
  },
  {
    phase: 'Days 61-90',
    title: 'Deliver measurable value',
    actions: [
      'Present a practical performance improvement plan tied to schedule, quality, risk, staffing, reporting, and stakeholder confidence.',
      'Implement a continuous-improvement backlog with owners, dates, metrics, and mission impact for each improvement action.',
      'Establish a sustainable operating rhythm that makes performance, risk, quality, and coverage visible before issues become escalations.',
    ],
  },
];

const missionAlignment = [
  {
    need: 'Accurate, timely vetting support',
    value:
      'Led federal data, verification, reporting, and mission-support systems where quality, timeliness, traceability, and operational trust mattered.',
  },
  {
    need: 'Program control and accountability',
    value:
      'Managed federal program execution across cost, schedule, risk, staffing, quality, stakeholder reporting, and executive communications.',
  },
  {
    need: 'Sensitive-data and secure-delivery mindset',
    value:
      'Brings active DHS / DoD TS/SCI clearance and experience with federal DevSecOps, data governance, identity, verification, and compliance-oriented environments.',
  },
  {
    need: 'Stakeholder confidence',
    value:
      'Served as a primary federal client interface with weekly reviews, executive briefings, dependency management, issue resolution, and transparent delivery reporting.',
  },
];

const valueDrivers = [
  'Operational leadership: stabilize staffing, schedules, deliverables, coverage, risks, and execution rhythm.',
  'Quality and compliance: improve QA review loops, evidence traceability, deliverable consistency, audit readiness, and management follow-through.',
  'Stakeholder management: build trust through clear communications, predictable reporting, proactive escalation, and mission-first prioritization.',
  'Data-driven program management: use dashboards, KPIs, trends, root-cause analysis, and decision logs to support leadership decisions.',
  'Secure modernization mindset: bring DevSecOps and data-strategy experience while respecting FBI-controlled systems, official interfaces, and sensitive architecture boundaries.',
];

const aiEfficiencyCards = [
  {
    label: 'Operational efficiency',
    title: 'Reduce administrative drag',
    text:
      'Use AI-assisted drafting, summarization, meeting-note synthesis, action tracking, and briefing preparation to reduce time spent on repetitive program-management tasks while keeping final products human-reviewed.',
  },
  {
    label: 'Quality consistency',
    title: 'Strengthen QA and SOP adherence',
    text:
      'Apply controlled AI assistants to surface relevant SOPs, check deliverables against required elements, identify missing evidence, and help analysts and reviewers apply consistent quality standards.',
  },
  {
    label: 'Leadership insight',
    title: 'Make risk visible earlier',
    text:
      'Use AI-enabled trend analysis across authorized operational metrics to highlight workload pressure, SLA risk, QA rework patterns, staffing gaps, and recurring blockers before they become escalations.',
  },
];

const aiUseCases = [
  'Program reporting: draft weekly status narratives from approved metrics, decision logs, risks, actions, and accomplishments for PM review.',
  'Knowledge retrieval: help staff find relevant SOPs, PWS requirements, briefing templates, quality standards, and prior approved examples faster.',
  'QA support: compare deliverables against checklists and flag missing sections, unclear language, inconsistent dates, or incomplete evidence references.',
  'Workload triage: summarize case-flow and staffing indicators so leads can spot bottlenecks, backlog pressure, coverage gaps, and handoff delays.',
  'Onboarding support: create role-specific learning paths, quick-reference guides, and scenario-based training from approved program materials.',
  'Stakeholder communications: prepare concise briefing drafts, risk summaries, action-item rollups, and executive-ready talking points for human approval.',
];

const aiGuardrails = [
  'Human-in-the-loop by design: AI supports program staff, analysts, QA, and leadership; it does not make adjudicative, suitability, clearance, or law-enforcement decisions.',
  'Approved environment only: no FBI, CJI, PII, classified, or sensitive program data should be placed into public AI tools or unapproved services.',
  'Auditability: AI-assisted outputs should preserve source references, reviewer identity, timestamps, prompt/output records where appropriate, and final human approval.',
  'Data minimization: use the minimum authorized information necessary for each workflow, with role-based access and strict separation of sensitive datasets.',
  'Policy alignment: implement only within FBI, CJIS, contract, privacy, records-retention, security, and agency AI governance requirements.',
  'Bias and quality control: measure error rates, review hallucination risk, test prompts against approved standards, and keep the authoritative decision with trained personnel.',
];

const aiRoadmap = [
  {
    phase: 'Phase 1',
    title: 'Govern and baseline',
    actions: [
      'Inventory repetitive PM, QA, reporting, onboarding, and knowledge-management tasks that consume staff time.',
      'Confirm data categories, authorities, approved environments, access controls, audit needs, and contract constraints.',
      'Define baseline metrics for reporting cycle time, QA rework, onboarding duration, action-item closure, and SLA-risk visibility.',
    ],
  },
  {
    phase: 'Phase 2',
    title: 'Pilot low-risk assistants',
    actions: [
      'Start with sanitized or approved materials such as SOP lookup, briefing templates, action logs, risk logs, and training content.',
      'Pilot AI-assisted status-report drafting, QA checklist review, meeting-summary generation, and knowledge retrieval with human approval.',
      'Measure time saved, output quality, reviewer corrections, user adoption, and compliance exceptions before expanding.',
    ],
  },
  {
    phase: 'Phase 3',
    title: 'Scale with controls',
    actions: [
      'Integrate approved AI workflows into dashboards, ticket queues, QA reviews, onboarding, and stakeholder reporting rhythms.',
      'Create recurring model/prompt reviews, access reviews, audit reports, and continuous-improvement backlogs.',
      'Scale only the use cases that prove measurable value, strong governance, user trust, and mission-safe performance.',
    ],
  },
];

const aiSuccessMetrics = [
  'Shorter weekly and monthly reporting preparation cycles.',
  'Lower QA rework caused by missing fields, inconsistent language, or incomplete evidence references.',
  'Faster analyst and reviewer access to SOPs, templates, and approved program guidance.',
  'Earlier visibility into workload bottlenecks, SLA risk, staffing pressure, and recurring blockers.',
  'Reduced onboarding time for analysts, QA reviewers, deputy PMs, and support staff.',
  'More consistent stakeholder briefings with clearer risks, decisions, actions, owners, and due dates.',
];

const menuItems = [
  {
    key: 'overview',
    icon: <FileSearchOutlined />,
    label: <a href="#overview">EVC Overview</a>,
  },
  {
    key: 'evc-summary',
    icon: <BankOutlined />,
    label: <a href="#evc-summary">EVC Summary</a>,
  },
  {
    key: 'mission-value',
    icon: <BarChartOutlined />,
    label: <a href="#mission-value">Mission Value</a>,
  },
  {
    key: 'ai-efficiency',
    icon: <SafetyCertificateOutlined />,
    label: <a href="#ai-efficiency">AI Efficiency</a>,
  },
  {
    key: 'nncp',
    icon: <FileSearchOutlined />,
    label: <a href="#nncp">NNCP</a>,
  },
  {
    key: 'ngi-detail',
    icon: <SafetyCertificateOutlined />,
    label: <a href="#ngi-detail">NGI Detail</a>,
  },
  {
    key: 'role',
    icon: <TeamOutlined />,
    label: <a href="#role">Role Snapshot</a>,
  },
  {
    key: 'requirements',
    icon: <SafetyCertificateOutlined />,
    label: <a href="#requirements">Requirements</a>,
  },
  {
    key: 'match',
    icon: <IdcardOutlined />,
    label: <a href="#match">Resume Match</a>,
  },
  {
    key: 'cjis',
    icon: <FileSearchOutlined />,
    label: <a href="#cjis">CJIS</a>,
  },
];

function extractItemText(item) {
  if (typeof item === 'string') return item;
  if (typeof item !== 'object' || item === null) return '';
  const parts = [];
  if (item.label) parts.push(item.label);
  if (item.title) parts.push(item.title);
  if (item.name) parts.push(item.name);
  if (item.value) parts.push(item.value);
  if (item.text) parts.push(item.text);
  if (item.detail) parts.push(item.detail);
  if (item.description) parts.push(item.description);
  if (item.category) parts.push(item.category);
  if (item.details) parts.push(item.details);
  if (item.layer) parts.push(item.layer);
  if (item.choice) parts.push(item.choice);
  if (item.rationale) parts.push(item.rationale);
  if (item.phase) parts.push(item.phase);
  if (item.need) parts.push(item.need);
  if (item.requirement) parts.push(item.requirement);
  if (item.evidence) parts.push(item.evidence);
  if (item.stat) parts.push(item.stat);
  if (item.strength) parts.push(item.strength);
  if (Array.isArray(item.actions)) parts.push(item.actions.join(' '));
  return parts.join('. ');
}

function buildSearchIndex(sections) {
  const index = [];
  sections.forEach(({ title, href, data }) => {
    data.forEach((item) => {
      const text = extractItemText(item);
      if (text) index.push({ title, text, href });
    });
  });
  return index;
}

function App() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const searchIndex = buildSearchIndex([
    { title: 'EVC Overview', href: '#overview', data: evcHighlights },
    { title: 'EVC Overview', href: '#overview', data: evcSummaryDetails },
    { title: 'EVC Overview', href: '#overview', data: evcFederalRegisterPoints },
    { title: 'EVC Overview', href: '#overview', data: evcVettingRelationship },
    { title: 'EVC Overview', href: '#overview', data: evcPublicInfoLimits },
    { title: 'EVC Overview', href: '#overview-distinction', data: evcDistinctionCards },
    { title: 'EVC Summary', href: '#evc-summary', data: unofficialSummaryCards },
    { title: 'EVC Summary', href: '#evc-summary', data: unofficialSummaryPoints },
    { title: 'Program Brief', href: '#program-brief', data: evcSummaryDetails },
    { title: 'Mission Value', href: '#mission-value', data: decisionMakerStats },
    { title: 'Mission Value', href: '#mission-value', data: executiveValueCards },
    { title: 'Mission Value', href: '#mission-value', data: first90DayPlan },
    { title: 'Mission Value', href: '#mission-value', data: missionAlignment },
    { title: 'Mission Value', href: '#mission-value', data: valueDrivers },
    { title: 'AI Efficiency', href: '#ai-efficiency', data: aiEfficiencyCards },
    { title: 'AI Efficiency', href: '#ai-efficiency', data: aiUseCases },
    { title: 'AI Efficiency', href: '#ai-efficiency', data: aiGuardrails },
    { title: 'AI Efficiency', href: '#ai-efficiency', data: aiRoadmap },
    { title: 'AI Efficiency', href: '#ai-efficiency', data: aiSuccessMetrics },
    { title: 'NNCP', href: '#nncp', data: nncpSummaryCards },
    { title: 'NNCP', href: '#nncp', data: nncpWhat },
    { title: 'NNCP', href: '#nncp', data: nncpProcess },
    { title: 'NNCP', href: '#nncp', data: nncpUses },
    { title: 'NNCP', href: '#nncp', data: nncpDistinctions },
    { title: 'NNCP', href: '#nncp', data: nncpPolicyIssues },
    { title: 'NGI Detail', href: '#ngi-detail', data: ngiCoreSystems },
    { title: 'NGI Detail', href: '#ngi-detail', data: ngiRepositoryFiles },
    { title: 'NGI Detail', href: '#ngi-detail', data: ngiRecordStructure },
    { title: 'NGI Detail', href: '#ngi-detail', data: ngiFileDefinitionPoints },
    { title: 'NGI Detail', href: '#ngi-detail', data: ngiCapabilityPoints },
    { title: 'NGI Detail', href: '#ngi-detail', data: ncicArchitecturePoints },
    { title: 'NGI Detail', href: '#ngi-detail', data: rapBackWorkflowPoints },
    { title: 'NGI Detail', href: '#ngi-detail', data: ngiScalePoints },
    { title: 'NGI Detail', href: '#ngi-detail', data: ngiIdentifierKeys },
    { title: 'NGI Detail', href: '#ngi-detail', data: ngiMissionUses },
    { title: 'NGI Detail', href: '#ngi-detail', data: ngiArchitecturePoints },
    { title: 'NGI Detail', href: '#ngi-detail', data: ngiSchemaDesignPoints },
    { title: 'NGI Detail', href: '#ngi-detail', data: ngiWorkflowFlows },
    { title: 'NGI Detail', href: '#ngi-detail', data: techStackLayers },
    { title: 'Role Snapshot', href: '#role', data: roleStats },
    { title: 'Role Snapshot', href: '#role', data: responsibilities },
    { title: 'Requirements', href: '#requirements', data: qualifications },
    { title: 'Resume Match', href: '#match', data: resumeMatchStats },
    { title: 'Resume Match', href: '#match', data: leadershipMatches },
    { title: 'Resume Match', href: '#match', data: qualificationMatches },
    { title: 'CJIS', href: '#cjis', data: cjisOverview },
    { title: 'CJIS', href: '#cjis', data: cjisSystems },
    { title: 'CJIS', href: '#cjis', data: cjisImportance },
  ]);

  const handleSearch = (value) => {
    setSearchQuery(value);
    if (!value.trim()) {
      setSearchResults([]);
      return;
    }
    const rawQuery = value.toLowerCase();
    const queryWords = rawQuery.split(/\s+/).filter((w) => w.length > 0);
    const results = searchIndex.filter((item) => {
      const title = item.title.toLowerCase();
      const text = item.text.toLowerCase();
      if (title.includes(rawQuery) || text.includes(rawQuery)) return true;
      return queryWords.some((word) => title.includes(word) || text.includes(word));
    });
    setSearchResults(results);
  };

  const openResult = (href) => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    window.location.hash = href.replace('#', '');
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#174ea6',
          colorInfo: '#174ea6',
          colorLink: '#0b3a75',
          colorText: '#0f172a',
          colorTextSecondary: '#334155',
          colorBorder: '#cbd5e1',
          colorBgLayout: '#f4f7fb',
          controlOutline: '#f59e0b',
          borderRadius: 16,
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        },
        components: {
          Layout: {
            headerBg: '#071224',
            siderBg: '#0f172a',
          },
        },
      }}
    >
      <Layout className="app-shell">
        <Sider
          breakpoint="lg"
          className="app-sider"
          collapsedWidth="0"
          width={286}
        >
          <div className="sider-brand">
            <div className="brand-mark">EVC</div>
            <div>
              <Text className="brand-kicker">FBI</Text>
              <Title className="brand-title" level={4}>
                Enterprise Vetting Center
              </Title>
            </div>
          </div>

          <Menu
            className="sider-menu"
            defaultSelectedKeys={['overview']}
            items={menuItems}
            mode="inline"
            theme="dark"
          />

          <Card className="sider-card" bordered={false}>
            <Tag color="gold">Listing</Tag>
            <Title level={5}>Program Manager FBI EVC</Title>
            <Paragraph>
              Public listing summary for the Winchester, Virginia role.
            </Paragraph>
          </Card>
        </Sider>

        <Layout>
          <Header className="app-header">
            <Space direction="vertical" size={0}>
              <Text className="header-label">Informational program brief</Text>
              <Text className="header-title">EVC Program Manager Overview</Text>
            </Space>
            <Space className="header-actions" size="middle">
              <Button
                icon={<SearchOutlined />}
                onClick={() => setSearchOpen(true)}
                type="default"
              >
                Search
              </Button>
              <Button href="#nncp">National Name Check Program</Button>
              <Button href="#role" type="primary">
                View role
              </Button>
            </Space>
          </Header>

          <Modal
            className="search-modal"
            footer={null}
            onCancel={() => {
              setSearchOpen(false);
              setSearchQuery('');
              setSearchResults([]);
            }}
            open={searchOpen}
            title="Search this dashboard"
            width={640}
          >
            <Input
              allowClear
              autoFocus
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search topics, systems, requirements, or value propositions..."
              prefix={<SearchOutlined />}
              value={searchQuery}
            />
            {searchResults.length > 0 && (
              <List
                className="search-results"
                dataSource={searchResults}
                renderItem={(item) => (
                  <List.Item>
                    <div
                      className="search-result-item"
                      onClick={() => openResult(item.href)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') openResult(item.href);
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <Text strong>{item.title}</Text>
                      <Text type="secondary"> — {item.text}</Text>
                    </div>
                  </List.Item>
                )}
                size="small"
              />
            )}
            {searchQuery.trim() && searchResults.length === 0 && (
              <Paragraph className="search-empty">
                No results found for “{searchQuery}”.
              </Paragraph>
            )}
          </Modal>

          <Content className="app-content">
            <section className="hero-panel">
              <Row align="middle" gutter={[32, 32]}>
                <Col lg={15} xs={24}>
                  <Space direction="vertical" size="large">
                    <Tag color="processing" icon={<BankOutlined />}>
                      FBI Information Management Division
                    </Tag>
                    <div>
                      <Title className="hero-title">
                        FBI Enterprise Vetting Center Program Manager
                      </Title>
                      <Paragraph className="hero-copy">
                        A structured Ant Design dashboard for the EVC mission,
                        National Name Check Program context, and the ANASEC
                        Program Manager role supporting analytical services at
                        the EVC Vetting Branch.
                      </Paragraph>
                    </div>
                    <Space wrap>
                      <Button href="#overview" size="large" type="primary">
                        Explore overview
                      </Button>
                      <Button href="#mission-value" size="large">
                        Why Russell
                      </Button>
                      <Button href="#requirements" size="large">
                        See requirements
                      </Button>
                    </Space>
                  </Space>
                </Col>

                <Col lg={9} xs={24}>
                  <Card className="comp-card" bordered={false}>
                    <div className="comp-stat-title">Resume alignment</div>
                    <div className="comp-stat-value">Strong</div>
                    <Divider />
                    <Paragraph>
                      Active DHS / DoD TS/SCI clearance, 18 years of federal IT
                      experience, $68M USCIS program leadership, and direct DHS
                      verification, data strategy, and stakeholder engagement
                      experience align well with the FBI EVC Program Manager
                      scope.
                    </Paragraph>
                  </Card>
                </Col>
              </Row>
            </section>

            <section className="content-section" id="program-brief">
              <Card className="list-card" bordered={false}>
                <Space className="section-heading" direction="vertical" size={6}>
                  <Text className="eyebrow">Program brief</Text>
                  <Title level={2}>Executive summary</Title>
                  <Paragraph>
                    This dashboard is an unofficial working brief for the FBI
                    Enterprise Vetting Center (EVC) Program Manager role with
                    ANASEC. It brings together public EVC mission context,
                    National Name Check Program background, CJIS systems
                    overview, FBI database architecture, role requirements,
                    resume alignment, and a responsible AI efficiency plan in
                    one decision-ready resource.
                  </Paragraph>
                </Space>
              </Card>
            </section>

            <section className="content-section" id="evc-summary">
              <Card className="summary-card" bordered={false}>
                <Space className="section-heading" direction="vertical" size={6}>
                  <Text className="eyebrow">Unofficial EVC summary</Text>
                  <Title level={2}>How to read this dashboard</Title>
                  <Paragraph>
                    This page is an unofficial working brief that brings
                    together public FBI EVC context, NNCP background, CJIS
                    distinctions, the ANASEC Program Manager role, and resume
                    alignment notes in one place.
                  </Paragraph>
                </Space>

                <Row gutter={[18, 18]}>
                  {unofficialSummaryCards.map((item) => (
                    <Col key={item.title} lg={8} xs={24}>
                      <Card className="summary-info-card" bordered={false}>
                        <Text className="card-label">{item.label}</Text>
                        <Title level={4}>{item.title}</Title>
                        <Paragraph>{item.text}</Paragraph>
                      </Card>
                    </Col>
                  ))}
                </Row>

                <a className="detail-card-link" href="#ngi-detail">
                  <Card className="detail-jump-card" bordered={false}>
                    <Row align="middle" gutter={[18, 18]}>
                      <Col lg={18} xs={24}>
                        <Text className="card-label">Click for detail page</Text>
                        <Title level={4}>
                          NGI, NCIC, keys, data scale, and background-check
                          architecture
                        </Title>
                        <Paragraph>
                          Open a focused detail page on the FBI systems behind
                          biometric and name-based checks, including NGI, NCIC,
                          Rap Back, identifier keys, mission uses, and a
                          practical automation architecture.
                        </Paragraph>
                      </Col>
                      <Col className="detail-card-cta" lg={6} xs={24}>
                        <span>View detail page</span>
                      </Col>
                    </Row>
                  </Card>
                </a>

                <Card className="summary-list-card" bordered={false}>
                  <Title level={4}>What this summary covers</Title>
                  <List
                    dataSource={unofficialSummaryPoints}
                    renderItem={(item) => (
                      <List.Item>
                        <List.Item.Meta
                          avatar={<CheckCircleOutlined />}
                          description={item}
                        />
                      </List.Item>
                    )}
                  />
                </Card>

                <Tabs
                  className="summary-tabs"
                  defaultActiveKey="evc"
                  items={[
                    {
                      key: 'evc',
                      label: 'What EVC Is',
                      children: (
                        <List
                          dataSource={evcSummaryDetails}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<BankOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'federal-register',
                      label: 'Federal Register Context',
                      children: (
                        <List
                          dataSource={evcFederalRegisterPoints}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<FileSearchOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'relationship',
                      label: 'EVC and NNCP',
                      children: (
                        <List
                          dataSource={evcVettingRelationship}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<SafetyCertificateOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'limits',
                      label: 'Public Info Limits',
                      children: (
                        <List
                          dataSource={evcPublicInfoLimits}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<CheckCircleOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'sources',
                      label: 'Sources',
                      children: (
                        <Space wrap>
                          {evcSummarySources.map((source) => (
                            <Button
                              href={source.href}
                              key={source.label}
                              rel="noreferrer"
                              target="_blank"
                            >
                              {source.label}
                            </Button>
                          ))}
                        </Space>
                      ),
                    },
                  ]}
                />
              </Card>
            </section>

            <section className="content-section" id="ngi-detail">
              <Card className="ngi-card" bordered={false}>
                <Space className="section-heading" direction="vertical" size={6}>
                  <Text className="eyebrow">Systems detail page</Text>
                  <Title level={2}>NGI, NCIC, keys, and vetting architecture</Title>
                  <Paragraph>
                    The key FBI system behind biometric background checks is
                    Next Generation Identification, supported by NCIC, FBI
                    identification records, e-QIP/DCSA workflows, and Rap Back
                    notification services. For expansion, NGI and NCIC are
                    large, modular, event-driven systems that store multimodal
                    biometrics and criminal-justice data, integrate with state
                    and federal partners, and expose tightly controlled services
                    for identity, background checks, and continuous vetting.
                  </Paragraph>
                </Space>

                <Row gutter={[18, 18]}>
                  {backgroundSystemCards.map((item) => (
                    <Col key={item.title} lg={8} xs={24}>
                      <Card className="ngi-system-card" bordered={false}>
                        <Text className="card-label">{item.label}</Text>
                        <Title level={4}>{item.title}</Title>
                        <Paragraph>{item.text}</Paragraph>
                      </Card>
                    </Col>
                  ))}
                </Row>

                <Tabs
                  className="ngi-tabs"
                  defaultActiveKey="systems"
                  items={[
                    {
                      key: 'systems',
                      label: 'Core Databases',
                      children: (
                        <List
                          dataSource={ngiCoreSystems}
                          grid={{ gutter: 18, lg: 2, md: 2, xs: 1 }}
                          renderItem={(item) => (
                            <List.Item>
                              <Card className="ngi-mini-card" bordered={false}>
                                <Title level={4}>{item.name}</Title>
                                <Paragraph>{item.description}</Paragraph>
                              </Card>
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'ngi-files',
                      label: 'NGI Files',
                      children: (
                        <List
                          dataSource={ngiRepositoryFiles}
                          grid={{ gutter: 18, lg: 2, md: 2, xs: 1 }}
                          renderItem={(item) => (
                            <List.Item>
                              <Card className="ngi-mini-card" bordered={false}>
                                <Title level={4}>{item.name}</Title>
                                <Paragraph>{item.description}</Paragraph>
                              </Card>
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'record-structure',
                      label: 'Record Structure',
                      children: (
                        <List
                          dataSource={ngiRecordStructure}
                          grid={{ gutter: 18, lg: 2, md: 2, xs: 1 }}
                          renderItem={(item) => (
                            <List.Item>
                              <Card className="ngi-mini-card" bordered={false}>
                                <Text className="card-label">{item.category}</Text>
                                <Paragraph>{item.details}</Paragraph>
                              </Card>
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'file-definition',
                      label: 'File Definition',
                      children: (
                        <List
                          dataSource={ngiFileDefinitionPoints}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<FileSearchOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'capabilities',
                      label: 'NGI Capabilities',
                      children: (
                        <List
                          dataSource={ngiCapabilityPoints}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<CheckCircleOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'scale',
                      label: 'Scale',
                      children: (
                        <List
                          dataSource={ngiScalePoints}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<BarChartOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'ncic',
                      label: 'NCIC',
                      children: (
                        <List
                          dataSource={ncicArchitecturePoints}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<FileSearchOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'keys',
                      label: 'Key Identifiers',
                      children: (
                        <List
                          dataSource={ngiIdentifierKeys}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<IdcardOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'rap-back',
                      label: 'Rap Back',
                      children: (
                        <List
                          dataSource={rapBackWorkflowPoints}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<SafetyCertificateOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'uses',
                      label: 'Mission Uses',
                      children: (
                        <List
                          dataSource={ngiMissionUses}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<SafetyCertificateOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'architecture',
                      label: 'Architecture',
                      children: (
                        <List
                          dataSource={ngiArchitecturePoints}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<FileSearchOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'workflow',
                      label: 'Workflow',
                      children: (
                        <List
                          dataSource={ngiWorkflowFlows}
                          grid={{ gutter: 18, lg: 2, md: 2, xs: 1 }}
                          renderItem={(item) => (
                            <List.Item>
                              <Card className="ngi-mini-card" bordered={false}>
                                <Title level={4}>{item.name}</Title>
                                <Paragraph>{item.description}</Paragraph>
                              </Card>
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'tech-stack',
                      label: 'Tech Stack',
                      children: (
                        <List
                          dataSource={techStackLayers}
                          grid={{ gutter: 18, lg: 2, md: 2, xs: 1 }}
                          renderItem={(item) => (
                            <List.Item>
                              <Card className="ngi-mini-card" bordered={false}>
                                <Text className="card-label">{item.layer}</Text>
                                <Title level={4}>{item.choice}</Title>
                                <Paragraph>{item.rationale}</Paragraph>
                              </Card>
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'schema',
                      label: 'Schema Design',
                      children: (
                        <List
                          dataSource={ngiSchemaDesignPoints}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<CheckCircleOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'sources',
                      label: 'Sources',
                      children: (
                        <Space wrap>
                          {ngiSources.map((source) => (
                            <Button
                              href={source.href}
                              key={source.label}
                              rel="noreferrer"
                              target="_blank"
                            >
                              {source.label}
                            </Button>
                          ))}
                        </Space>
                      ),
                    },
                  ]}
                />
              </Card>
            </section>

            <section className="content-section" id="mission-value">
              <Card className="value-card" bordered={false}>
                <Space className="section-heading" direction="vertical" size={6}>
                  <Text className="eyebrow">Decision-maker brief</Text>
                  <Title level={2}>
                    Why Russell Riehl for EVC Program Manager
                  </Title>
                  <Paragraph>
                    Russell brings 18 years of federal mission delivery, active
                    DHS / DoD TS/SCI clearance, large-team leadership, data and
                    DevSecOps modernization experience, and program discipline
                    that can help ANASEC and FBI EVC strengthen operational
                    execution, reporting, quality control, and stakeholder trust.
                  </Paragraph>
                </Space>

                <Row gutter={[18, 18]}>
                  {decisionMakerStats.map((item) => (
                    <Col key={item.label} lg={6} md={12} xs={24}>
                      <Card className="value-stat-card" bordered={false}>
                        <div className="value-stat-label">{item.label}</div>
                        <div className="value-stat-value">{item.value}</div>
                        <Paragraph>{item.detail}</Paragraph>
                      </Card>
                    </Col>
                  ))}
                </Row>

                <Row className="value-row" gutter={[18, 18]}>
                  {executiveValueCards.map((item) => (
                    <Col key={item.title} lg={8} xs={24}>
                      <Card className="value-info-card" bordered={false}>
                        <Text className="card-label">{item.label}</Text>
                        <Title level={4}>{item.title}</Title>
                        <Paragraph>{item.text}</Paragraph>
                      </Card>
                    </Col>
                  ))}
                </Row>

                <Tabs
                  className="value-tabs"
                  defaultActiveKey="first-90"
                  items={[
                    {
                      key: 'first-90',
                      label: 'First 90 Days',
                      children: (
                        <List
                          dataSource={first90DayPlan}
                          grid={{ gutter: 18, lg: 3, md: 1, xs: 1 }}
                          renderItem={(phase) => (
                            <List.Item>
                              <Card className="value-plan-card" bordered={false}>
                                <Text className="card-label">{phase.phase}</Text>
                                <Title level={4}>{phase.title}</Title>
                                <List
                                  dataSource={phase.actions}
                                  renderItem={(action) => (
                                    <List.Item>
                                      <List.Item.Meta
                                        avatar={<CheckCircleOutlined />}
                                        description={action}
                                      />
                                    </List.Item>
                                  )}
                                />
                              </Card>
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'mission-alignment',
                      label: 'Mission Alignment',
                      children: (
                        <List
                          dataSource={missionAlignment}
                          grid={{ gutter: 18, lg: 2, md: 2, xs: 1 }}
                          renderItem={(item) => (
                            <List.Item>
                              <Card className="value-info-card" bordered={false}>
                                <Text className="card-label">Mission need</Text>
                                <Title level={4}>{item.need}</Title>
                                <Paragraph>{item.value}</Paragraph>
                              </Card>
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'value-drivers',
                      label: 'How I Bring Value',
                      children: (
                        <List
                          dataSource={valueDrivers}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<BarChartOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                  ]}
                />
              </Card>
            </section>

            <section className="content-section" id="ai-efficiency">
              <Card className="ai-card" bordered={false}>
                <Space className="section-heading" direction="vertical" size={6}>
                  <Text className="eyebrow">Responsible AI efficiency</Text>
                  <Title level={2}>
                    Bringing AI to improve program speed, quality, and insight
                  </Title>
                  <Paragraph>
                    The opportunity is not to automate adjudicative judgment. It
                    is to use governed, human-reviewed AI to reduce administrative
                    load, improve quality consistency, accelerate knowledge
                    retrieval, and give leadership earlier visibility into risks,
                    workload pressure, and process bottlenecks.
                  </Paragraph>
                </Space>

                <Row gutter={[18, 18]}>
                  {aiEfficiencyCards.map((item) => (
                    <Col key={item.title} lg={8} xs={24}>
                      <Card className="ai-info-card" bordered={false}>
                        <Text className="card-label">{item.label}</Text>
                        <Title level={4}>{item.title}</Title>
                        <Paragraph>{item.text}</Paragraph>
                      </Card>
                    </Col>
                  ))}
                </Row>

                <Tabs
                  className="ai-tabs"
                  defaultActiveKey="use-cases"
                  items={[
                    {
                      key: 'use-cases',
                      label: 'Efficiency Use Cases',
                      children: (
                        <List
                          dataSource={aiUseCases}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<BarChartOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'guardrails',
                      label: 'Mission Guardrails',
                      children: (
                        <List
                          dataSource={aiGuardrails}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<SafetyCertificateOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'roadmap',
                      label: 'Implementation Roadmap',
                      children: (
                        <List
                          dataSource={aiRoadmap}
                          grid={{ gutter: 18, lg: 3, md: 1, xs: 1 }}
                          renderItem={(phase) => (
                            <List.Item>
                              <Card className="ai-plan-card" bordered={false}>
                                <Text className="card-label">{phase.phase}</Text>
                                <Title level={4}>{phase.title}</Title>
                                <List
                                  dataSource={phase.actions}
                                  renderItem={(action) => (
                                    <List.Item>
                                      <List.Item.Meta
                                        avatar={<CheckCircleOutlined />}
                                        description={action}
                                      />
                                    </List.Item>
                                  )}
                                />
                              </Card>
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'metrics',
                      label: 'Success Metrics',
                      children: (
                        <List
                          dataSource={aiSuccessMetrics}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<CheckCircleOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                  ]}
                />
              </Card>
            </section>

            <section className="content-section" id="overview">
              <Space className="section-heading" direction="vertical" size={6}>
                <Text className="eyebrow">Mission context</Text>
                <Title level={2}>What the EVC supports</Title>
                <Paragraph>
                  The Enterprise Vetting Center helps manage name-based checks
                  and vetting services that support government agency security
                  functions.
                </Paragraph>
              </Space>

              <Row gutter={[18, 18]}>
                {evcHighlights.map((item) => (
                  <Col key={item.label} lg={6} md={12} xs={24}>
                    <Card className="info-card" bordered={false}>
                      <Text className="card-label">{item.label}</Text>
                      <Paragraph>{item.value}</Paragraph>
                    </Card>
                  </Col>
                ))}
              </Row>

              <Row className="note-row" gutter={[18, 18]}>
                <Col md={12} xs={24}>
                  <Card bordered={false}>
                    <Title level={4}>EVC and CJIS distinction</Title>
                    <Paragraph>
                      EVC is distinct from the FBI CJIS Division, which handles
                      fingerprint-based criminal history checks, though both
                      operate within the broader federal security and vetting
                      framework.
                    </Paragraph>
                  </Card>
                </Col>
                <Col md={12} xs={24}>
                  <Card bordered={false}>
                    <Title level={4}>Technology modernization</Title>
                    <Paragraph>
                      The updated fee model includes an automation portion
                      intended to support technical infrastructure updates for
                      vetting operations.
                    </Paragraph>
                  </Card>
                </Col>
              </Row>

              <Row className="note-row" gutter={[18, 18]} id="overview-distinction">
                {evcDistinctionCards.map((item) => (
                  <Col key={item.title} lg={8} md={8} xs={24}>
                    <Card className="info-card" bordered={false}>
                      <Text className="card-label">{item.label}</Text>
                      <Title level={4}>{item.title}</Title>
                      <Paragraph>{item.text}</Paragraph>
                    </Card>
                  </Col>
                ))}
              </Row>
            </section>

            <section className="content-section" id="nncp">
              <Card className="nncp-card" bordered={false}>
                <Space className="section-heading" direction="vertical" size={6}>
                  <Text className="eyebrow">National Name Check Program</Text>
                  <Title level={2}>What NNCP is and how it supports vetting</Title>
                  <Paragraph>
                    The National Name Check Program is an FBI program that
                    searches FBI records for references to a person’s name and
                    provides those results to government agencies for security
                    vetting, background checks, and investigative support.
                  </Paragraph>
                </Space>

                <Row gutter={[18, 18]}>
                  {nncpSummaryCards.map((item) => (
                    <Col key={item.title} lg={8} xs={24}>
                      <Card className="nncp-summary-card" bordered={false}>
                        <Text className="card-label">{item.label}</Text>
                        <Title level={4}>{item.title}</Title>
                        <Paragraph>{item.text}</Paragraph>
                      </Card>
                    </Col>
                  ))}
                </Row>

                <Tabs
                  className="nncp-tabs"
                  defaultActiveKey="what"
                  items={[
                    {
                      key: 'what',
                      label: 'What NNCP Is',
                      children: (
                        <List
                          dataSource={nncpWhat}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<CheckCircleOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'process',
                      label: 'Core Process',
                      children: (
                        <List
                          dataSource={nncpProcess}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<FileSearchOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'uses',
                      label: 'How It Is Used',
                      children: (
                        <List
                          dataSource={nncpUses}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<SafetyCertificateOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'distinction',
                      label: 'Distinctions',
                      children: (
                        <List
                          dataSource={nncpDistinctions}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<IdcardOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'policy',
                      label: 'Policy Issues',
                      children: (
                        <List
                          dataSource={nncpPolicyIssues}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<TeamOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'sources',
                      label: 'Sources',
                      children: (
                        <Space wrap>
                          {nncpSources.map((source) => (
                            <Button
                              href={source.href}
                              key={source.label}
                              rel="noreferrer"
                              target="_blank"
                            >
                              {source.label}
                            </Button>
                          ))}
                        </Space>
                      ),
                    },
                  ]}
                />
              </Card>
            </section>

            <section className="content-section role-section" id="role">
              <Space className="section-heading" direction="vertical" size={6}>
                <Text className="eyebrow">Role snapshot</Text>
                <Title level={2}>ANASEC Program Manager at FBI EVC</Title>
                <Paragraph>
                  The position directs analytical support services, manages
                  contractor performance, and coordinates with EVC leadership to
                  meet operational priorities.
                </Paragraph>
              </Space>

              <Row gutter={[18, 18]}>
                {roleStats.map((item) => (
                  <Col key={item.label} lg={6} md={12} xs={24}>
                    <Card className="stat-card" bordered={false}>
                      <div className="role-stat-title">{item.label}</div>
                      <div className="role-stat-value">{item.value}</div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </section>

            <section className="content-section" id="requirements">
              <Row gutter={[18, 18]}>
                <Col lg={12} xs={24}>
                  <Card className="list-card" bordered={false}>
                    <Text className="eyebrow">Responsibilities</Text>
                    <Title level={2}>Program leadership scope</Title>
                    <List
                      dataSource={responsibilities}
                      renderItem={(item) => (
                        <List.Item>
                          <List.Item.Meta
                            avatar={<CheckCircleOutlined />}
                            description={item}
                          />
                        </List.Item>
                      )}
                    />
                  </Card>
                </Col>

                <Col lg={12} xs={24}>
                  <Card className="list-card accent-card" bordered={false}>
                    <Text className="eyebrow">Qualifications</Text>
                    <Title level={2}>Required background</Title>
                    <List
                      dataSource={qualifications}
                      renderItem={(item) => (
                        <List.Item>
                          <List.Item.Meta
                            avatar={<IdcardOutlined />}
                            description={item}
                          />
                        </List.Item>
                      )}
                    />
                  </Card>
                </Col>
              </Row>
            </section>

            <section className="content-section" id="match">
              <Card className="match-card" bordered={false}>
                <Space className="section-heading" direction="vertical" size={6}>
                  <Text className="eyebrow white-eyebrow">
                    Resume alignment
                  </Text>
                  <Title level={2}>Russell Riehl match to FBI EVC PM role</Title>
                  <Paragraph>
                    The resume presents a strong match for federal program
                    management, contractor-team leadership, data strategy,
                    DevSecOps delivery, stakeholder briefings, quality control,
                    and active cleared work.
                  </Paragraph>
                </Space>

                <Row gutter={[18, 18]}>
                  {resumeMatchStats.map((item) => (
                    <Col key={item.label} lg={6} md={12} xs={24}>
                      <Card className="match-stat-card" bordered={false}>
                        <div className="match-stat-title">{item.label}</div>
                        <div className="match-stat-value">{item.value}</div>
                      </Card>
                    </Col>
                  ))}
                </Row>

                <Tabs
                  className="match-tabs"
                  defaultActiveKey="leadership"
                  items={[
                    {
                      key: 'leadership',
                      label: 'Program leadership scope',
                      children: (
                        <List
                          dataSource={leadershipMatches}
                          renderItem={(item) => (
                            <List.Item>
                              <Card className="match-row-card" bordered={false}>
                                <Row gutter={[18, 18]}>
                                  <Col lg={7} xs={24}>
                                    <Text className="match-label">
                                      Requirement
                                    </Text>
                                    <Paragraph>{item.requirement}</Paragraph>
                                  </Col>
                                  <Col lg={12} xs={24}>
                                    <Text className="match-label">
                                      Resume evidence
                                    </Text>
                                    <Paragraph>{item.evidence}</Paragraph>
                                  </Col>
                                  <Col lg={5} xs={24}>
                                    <Text className="match-label">Strength</Text>
                                    <Tag
                                      className="strength-tag"
                                      color={
                                        item.strength === 'Strong'
                                          ? 'green'
                                          : 'gold'
                                      }
                                    >
                                      {item.strength}
                                    </Tag>
                                  </Col>
                                </Row>
                              </Card>
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'qualifications',
                      label: 'Qualifications',
                      children: (
                        <List
                          dataSource={qualificationMatches}
                          renderItem={(item) => (
                            <List.Item>
                              <Card className="match-row-card" bordered={false}>
                                <Row gutter={[18, 18]}>
                                  <Col lg={7} xs={24}>
                                    <Text className="match-label">
                                      Requirement
                                    </Text>
                                    <Paragraph>{item.requirement}</Paragraph>
                                  </Col>
                                  <Col lg={12} xs={24}>
                                    <Text className="match-label">
                                      Resume evidence
                                    </Text>
                                    <Paragraph>{item.evidence}</Paragraph>
                                  </Col>
                                  <Col lg={5} xs={24}>
                                    <Text className="match-label">Strength</Text>
                                    <Tag
                                      className="strength-tag"
                                      color={
                                        item.strength === 'Strong'
                                          ? 'green'
                                          : 'gold'
                                      }
                                    >
                                      {item.strength}
                                    </Tag>
                                  </Col>
                                </Row>
                              </Card>
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'positioning',
                      label: 'Resume positioning',
                      children: (
                        <Row gutter={[18, 18]}>
                          <Col lg={12} xs={24}>
                            <Card className="positioning-card" bordered={false}>
                              <Tag color="blue">Recommended headline</Tag>
                              <Paragraph>
                                Federal Program Manager with active DHS / DoD
                                Top Secret / SCI clearance, 18 years of IT
                                experience, and proven success leading DHS data,
                                verification, DevSecOps, and digital
                                transformation programs.
                              </Paragraph>
                            </Card>
                          </Col>
                          <Col lg={12} xs={24}>
                            <Card className="positioning-card accent-card" bordered={false}>
                              <Tag color="gold">Application note</Tag>
                              <Paragraph>
                                Confirm whether the active TS/SCI investigation
                                maps to T5/T5R and whether CE/CV enrollment is
                                already active or available through reciprocity.
                              </Paragraph>
                            </Card>
                          </Col>
                        </Row>
                      ),
                    },
                  ]}
                />
              </Card>
            </section>

            <section className="content-section" id="cjis">
              <Card className="tabs-card" bordered={false}>
                <Space className="section-heading" direction="vertical" size={6}>
                  <Text className="eyebrow">CJIS reference tab</Text>
                  <Title level={2}>
                    Criminal Justice Information Services Division
                  </Title>
                  <Paragraph>
                    CJIS is the FBI’s primary hub for managing and sharing
                    criminal justice data and identification services with law
                    enforcement, national security partners, and, in some cases,
                    the public.
                  </Paragraph>
                </Space>

                <Tabs
                  className="cjis-tabs"
                  defaultActiveKey="what"
                  items={[
                    {
                      key: 'what',
                      label: 'What CJIS Is',
                      children: (
                        <Row gutter={[18, 18]}>
                          {cjisOverview.map((item) => (
                            <Col key={item} lg={8} xs={24}>
                              <Card className="cjis-overview-card" bordered={false}>
                                <CheckCircleOutlined />
                                <Paragraph>{item}</Paragraph>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      ),
                    },
                    {
                      key: 'systems',
                      label: 'Core Systems',
                      children: (
                        <List
                          className="system-list"
                          dataSource={cjisSystems}
                          grid={{ gutter: 18, lg: 2, md: 2, xs: 1 }}
                          renderItem={(item) => (
                            <List.Item>
                              <Card className="system-card" bordered={false}>
                                <Title level={4}>{item.name}</Title>
                                <Paragraph>{item.description}</Paragraph>
                              </Card>
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'importance',
                      label: 'Why It Matters',
                      children: (
                        <List
                          dataSource={cjisImportance}
                          renderItem={(item) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<SafetyCertificateOutlined />}
                                description={item}
                              />
                            </List.Item>
                          )}
                        />
                      ),
                    },
                    {
                      key: 'perspective',
                      label: 'Perspective',
                      children: (
                        <Row gutter={[18, 18]}>
                          <Col lg={12} xs={24}>
                            <Card className="perspective-card" bordered={false}>
                              <Tag color="blue">Data/content</Tag>
                              <Title level={4}>What is in CJI?</Title>
                              <Paragraph>
                                Focus on criminal justice information content,
                                identity history, biometric identifiers,
                                investigative records, and data-sharing
                                workflows.
                              </Paragraph>
                            </Card>
                          </Col>
                          <Col lg={12} xs={24}>
                            <Card className="perspective-card accent-card" bordered={false}>
                              <Tag color="gold">Compliance/controls</Tag>
                              <Title level={4}>How is CJI protected?</Title>
                              <Paragraph>
                                Focus on CJIS Security Policy, cloud hosting,
                                vendor controls, access management, auditing,
                                encryption, and agency responsibilities.
                              </Paragraph>
                            </Card>
                          </Col>
                          <Col span={24}>
                            <Space wrap>
                              {cjisSources.map((source) => (
                                <Button
                                  href={source.href}
                                  key={source.label}
                                  rel="noreferrer"
                                  target="_blank"
                                >
                                  {source.label}
                                </Button>
                              ))}
                            </Space>
                          </Col>
                        </Row>
                      ),
                    },
                  ]}
                />
              </Card>
            </section>

          </Content>

          <Footer className="app-footer">
            <Space split={<Divider type="vertical" />}>
              <a href="#evc-summary">Unofficial EVC summary</a>
              <span>
                Built with{' '}
                <a href="https://react.dev/" rel="noreferrer" target="_blank">
                  React
                </a>{' '}
                and{' '}
                <a href="https://ant.design/" rel="noreferrer" target="_blank">
                  Ant Design
                </a>
              </span>
              <a href="#program-brief">
                <BarChartOutlined /> Program brief
              </a>
            </Space>
          </Footer>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
}

export default App;
